import { fabricThumbnailUrl } from '@/lib/images/fabricThumbnail';
import Link from 'next/link';
import Image from 'next/image';
import { Tag } from 'lucide-react';
import { promoPrice, type ActivePromo } from '@/lib/hooks/useActivePromo';
import { collectionPurchaseUrl } from '@/lib/catalog/collectionPurchaseUrl';
import type { PriceForSize } from '@/lib/catalog/priceForSizes';

/**
 * The one product card. Shared by the product listing and the homepage so a
 * product looks and prices the same wherever it appears — the consistency
 * the reference store gets from a single card template. Laid out like their card
 * (2026-10-18): borderless photo, offer badge, fabric swatches, name, stars,
 * price, and a short promise line.
 */
export interface CardProduct {
  product_id: number;
  name: string;
  slug: string;
  category_name: string;
  base_price: number;
  primary_image_url?: string;
  short_description?: string;
  is_featured?: boolean | number;
  avg_rating?: number | null;
  review_count?: number;
  /** Real purchasable minimum at the reference size (incl. freight). */
  starting_price?: number | null;
  /** The window size `starting_price` is quoted for, e.g. '36" x 60"'. */
  starting_price_basis?: string | null;
  /** First few fabrics a buyer can choose, and how many there are in total. */
  swatches?: { name: string; image: string }[];
  swatch_count?: number;
}

// Derive 1-2 feature pills from the product name + category. We don't have
// a structured "features" column yet, but the product names reliably contain
// these keywords. When the DB gets a proper `tags` column populated, swap
// this for tag-driven pills.
export function extractFeaturePills(name: string, categoryName?: string): string[] {
  const haystack = `${name} ${categoryName || ''}`.toLowerCase();
  const pills: string[] = [];
  if (/\bcordless\b/.test(haystack)) pills.push('Cordless');
  if (/\bmotorized|smart\b/.test(haystack)) pills.push('Motorized');
  if (/\bblackout\b/.test(haystack)) pills.push('Blackout');
  if (/\bcellular|honeycomb\b/.test(haystack)) pills.push('Honeycomb');
  if (/\bsolar|sunscreen|sheer\b/.test(haystack) && !pills.includes('Blackout')) pills.push('Light Filtering');
  if (/\boutdoor\b/.test(haystack)) pills.push('Outdoor');
  if (/\bzebra\b/.test(haystack)) pills.push('Day-Night');
  return pills.slice(0, 2);
}

// The price actually shown on the card. Filtering and sorting must use the same
// number, otherwise a "under $250" filter would test the old unpurchasable
// base_price (~$68) while the card displays the real one (~$210).
export function displayPrice(p: Pick<CardProduct, 'starting_price' | 'base_price'>): number {
  const s = Number(p.starting_price ?? 0);
  return s > 0 ? s : Number(p.base_price ?? 0);
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} viewBox="0 0 20 20" className={`w-4 h-4 ${rating >= i - 0.25 ? 'text-amber-400' : rating >= i - 0.75 ? 'text-amber-300' : 'text-gray-300'}`} fill="currentColor">
          <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

interface ProductCardProps {
  product: CardProduct;
  promo?: ActivePromo | null;
  imageSizes?: string;
  selectedSize?: PriceForSize;
}

export default function ProductCard({
  product,
  promo = null,
  imageSizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
  selectedSize,
}: ProductCardProps) {
  const shown = displayPrice(product);
  const basis = product.starting_price_basis;
  const swatches = product.swatches ?? [];
  const moreSwatches = Math.max(0, (product.swatch_count ?? swatches.length) - swatches.length);
  const reviews = product.review_count ?? 0;
  const rating = Number(product.avg_rating ?? 0);

  return (
    <Link href={collectionPurchaseUrl(product.slug, selectedSize)} className="group flex flex-col h-full">
      {/* Photo */}
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100">
        {product.primary_image_url ? (
          <Image
            src={product.primary_image_url}
            alt={product.name}
            fill
            sizes={imageSizes}
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            {product.category_name}
          </div>
        )}
        {/* Offer badge — only while a real promotion is running. */}
        {promo && shown > 0 && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-sale shadow-sm">
            <Tag size={12} aria-hidden="true" />
            {promo.discountPercent}% Off
          </span>
        )}
      </div>

      {/* Fabric swatches */}
      {swatches.length > 0 && (
        <div className="mt-3 flex items-center gap-1.5" aria-label={`${product.swatch_count ?? swatches.length} fabric colors`}>
          {swatches.map((s, i) => (
            <span
              key={s.image}
              title={s.name}
              className={`relative w-7 h-7 rounded overflow-hidden bg-gray-100 ${i === 0 ? 'ring-2 ring-gray-900 ring-offset-1' : 'ring-1 ring-gray-200'}`}
            >
              <Image
                src={fabricThumbnailUrl(s.image, 64)}
                alt=""
                fill
                sizes="28px"
                unoptimized
                className="object-cover"
                onError={(e) => { const box = e.currentTarget.parentElement; if (box) box.style.display = 'none'; }}
              />
            </span>
          ))}
          {moreSwatches > 0 && <span className="ml-1 text-xs text-gray-600">+{moreSwatches}</span>}
        </div>
      )}

      {/* Name */}
      <h3 className="mt-2 font-sans text-base text-gray-900 leading-snug line-clamp-2 group-hover:underline underline-offset-4">
        {product.name}
      </h3>

      {/* Stars — only from real web reviews, never in-store testimonials or seeds. */}
      {reviews > 0 && rating > 0 && (
        <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-700">
          <Stars rating={rating} />
          <span>{rating.toFixed(1)} ({reviews})</span>
        </div>
      )}

      {/* Price. `starting_price` is the real minimum a customer can buy at
          (category base + cheapest fabric + per-blind freight) at the
          reference size. No basis means the category could not be priced —
          usually no fabrics linked, so it can't be configured either — and the
          old base_price would be both unpurchasable and unreachable, so we ask
          for a quote instead of showing it. */}
      <div className="mt-2">
        {!basis ? (
          <p className="text-sm font-semibold text-gray-900">Contact us for a quote</p>
        ) : promo && shown > 0 ? (
          <p className="leading-tight">
            <span className="text-xl font-semibold tabular-nums text-sale">${promoPrice(shown, promo).toFixed(2)}</span>
            <span className="ml-2 text-sm text-gray-500 line-through tabular-nums">${shown.toFixed(2)}</span>
            <span className="block text-sm text-gray-600">for {basis} · with code {promo.code}</span>
          </p>
        ) : (
          <p className="leading-tight">
            <span className="text-xl font-semibold tabular-nums text-gray-900">${shown.toFixed(2)}</span>
            {/* Size anchor. Without it a "from" price on a made-to-measure
                product is uncomparable. */}
            <span className="block text-sm text-gray-600">for {basis}</span>
          </p>
        )}
      </div>

      <p className="mt-2 text-sm font-medium text-sale">Free Shipping · Made to Measure</p>
    </Link>
  );
}
